import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { v4 as uuidv4 } from "uuid";
import { QuoteNotesSidebarService } from "../../../../core/services/quote-notes-sidebar.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { SalesOrderNotes } from "../../../../core/models/orders/salesOrderNotes";
import { QuotesNotes } from "../../../../core/models/qoutes/quotesNotes";
import { LoaderService } from "../../../../core/services/loader.service";
import { DrawerModule } from "primeng/drawer";
import { SelectModule } from "primeng/select";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";

@UntilDestroy()
@Component({
  selector: "jni-quote-notes-sidebar",
  templateUrl: "./quote-notes-sidebar.component.html",
  styleUrls: ["./quote-notes-sidebar.component.scss"],
  imports: [
    DrawerModule,
    SelectModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
  ],
})
export class QuoteNotesSidebarComponent implements OnInit {
  @Input() display!: boolean;
  @Input() quoteId = "";
  @Input() rejectionReason = false;
  @Output() displayChange = new EventEmitter<boolean>();
  @Output() refreshOrderNotesGrid = new EventEmitter();
  @Output() closeSidebar = new EventEmitter();
  @Output() updateHeader = new EventEmitter();

  @Input() set type(val: string) {
    this.saleOrderNoteForm.patchValue({
      type: val,
      description: "Rejected",
    });
  }

  saleOrderNoteForm!: FormGroup;
  defaultTypeSelection = "";
  defaultPurchaseOrderSelection = "";
  typeList = [
    {
      id: 1,
      name: "Proposal",
    },
    {
      id: 2,
      name: "Billing note",
    },
    {
      id: 3,
      name: "Order note",
    },
  ];
  numberFieldStatus = true;
  purchaseOrderNumber: string[] = [];
  ispurchaseOrderIdField = false;

  constructor(
    private _formBuilder: FormBuilder,
    private _loaderService: LoaderService,
    private _toastr: JniToastrService,
    private _quoteNotesSidebarService: QuoteNotesSidebarService
  ) {
    this.saleOrderNoteForm = this._formBuilder.group({
      id: 0,
      salesOrderId: [""],
      type: ["", Validators.required],
      description: ["", Validators.required],
      note: [""],
    });
  }

  onHide() {
    this.displayChange.emit(false);
  }

  ngOnInit(): void {
    this.getAllLinePoNumber(this.quoteId);
  }

  patchValueInNotes(date: string, reason: string) {
    this.saleOrderNoteForm.patchValue({
      note: date + reason,
    });
  }

  // openPurchaseOrder(event: any) {
  //   if (event.target.checked) {
  //     this.numberFieldStatus = false;
  //     this.setCustomerPoRequiredField();
  //   } else {
  //     this.numberFieldStatus = true;
  //     this.removeCustomerPoRequiredField();
  //   }
  // }

  getAllLinePoNumber(quoteId: string) {
    if (quoteId) {
      this._quoteNotesSidebarService
        .getAllLinePoNumber(quoteId)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this.purchaseOrderNumber = res.data;
          },
        });
    }
  }

  setCustomerPoRequiredField() {
    this.ispurchaseOrderIdField = true;
    this.saleOrderNoteForm
      .get("purchaseOrderId")
      ?.setValidators(Validators.required);
    this.saleOrderNoteForm.get("purchaseOrderId")?.updateValueAndValidity();
  }

  removeCustomerPoRequiredField() {
    this.ispurchaseOrderIdField = false;
    this.saleOrderNoteForm.get("purchaseOrderId")?.clearValidators();
    this.saleOrderNoteForm.get("purchaseOrderId")?.updateValueAndValidity();
  }

  onQuoteNotesSave() {
    this._loaderService.setLoaderState(true);
    const saleOrderFormObject =
      this.saleOrderNoteForm.getRawValue() as QuotesNotes;
    if (this.quoteId) {
      saleOrderFormObject.salesOrderId = this.quoteId;
      saleOrderFormObject.portalKey = uuidv4();
    }

    if (this.rejectionReason) {
      this.closeSidebar.emit();
      this.updateHeader.emit(saleOrderFormObject);
      return;
    }

    this._quoteNotesSidebarService
      .AddQuoteNotes(saleOrderFormObject)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          this.clearOrderNoteForm();
          if (res.data) {
            this.closeSidebar.emit();
            this.refreshOrderNotesGrid.emit();
            this._toastr.success(
              "Success",
              res.message || "Order note added successfully"
            );
          }
        },
        error: res => {
          //this.clearOrderNoteForm();
          this._loaderService.setLoaderState(false);
          this._toastr.error("Error", res.message || "Error adding order note");
        },
      });
  }

  closeSidebarModel() {
    this.closeSidebar.emit();
  }

  patchValueInOrderNote(salesOrderNotes: SalesOrderNotes) {
    this.saleOrderNoteForm?.patchValue({
      id: salesOrderNotes.id,
      type: salesOrderNotes.type,
      purchaseOrderStatus: salesOrderNotes?.purchaseOrderId ? true : false,
      purchaseOrderId: salesOrderNotes.purchaseOrderId,
      description: salesOrderNotes.description,
      note: salesOrderNotes.note,
    });

    if (salesOrderNotes?.purchaseOrderId) {
      this.numberFieldStatus = false;
      this.setCustomerPoRequiredField();
    } else {
      this.numberFieldStatus = true;
      this.defaultPurchaseOrderSelection = "";
      this.removeCustomerPoRequiredField();
    }
  }

  clearOrderNoteForm() {
    this.numberFieldStatus = true;
    this.defaultPurchaseOrderSelection = "";
    this.removeCustomerPoRequiredField();
    this.saleOrderNoteForm?.patchValue({
      id: 0,
      type: "Proposal",
      purchaseOrderStatus: false,
      purchaseOrderId: "",
      description: "",
      note: "",
    });
  }

  onPurchaseOrderSelection(name: string) {
    if (name === "Billing note") {
      this.numberFieldStatus = false;
      this.setCustomerPoRequiredField();
      this.saleOrderNoteForm.patchValue({
        purchaseOrderStatus: true,
      });
    } else if (name === "Order note") {
      this.numberFieldStatus = false;
      this.setCustomerPoRequiredField();
      this.saleOrderNoteForm.patchValue({
        purchaseOrderStatus: true,
      });
    } else {
      this.numberFieldStatus = true;
      this.defaultPurchaseOrderSelection = "";
      this.removeCustomerPoRequiredField();
      this.saleOrderNoteForm.patchValue({
        purchaseOrderStatus: false,
      });
    }
  }
}
